/**
 * Module list/form permissions scoped to toolbar-selected company.
 *
 * Unlike `useMenuPermissions` (login company → sidebar/menu) or
 * `usePermissionChecker` / `useXPermissions` (auth.selectedCompany → login context),
 * this hook resolves CRUD from `auth.companies[].roles` for the company id
 * passed via toolbar filter (`activeCompanyFilter` / `companyId`).
 */

import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import type { CompanyData } from '@/modules/auth/helpers/CompanyStorage';
import { getCurrentCompanyId } from '@/modules/auth/helpers/CompanyStorage';
import { RootState } from '@/setup';
import type { CompanyPermissionSummary } from '../utils/moduleCompanyPermissions';
import {
  hasPermissionInCompany,
  resolveCompanyPermissions,
} from '../utils/moduleCompanyPermissions';

export interface UseModuleCompanyPermissionsOptions {
  resource: string;
  /** Toolbar company: UUID, `all`, `global`, or empty */
  companyId?: string | null;
  moduleKey?: string;
}

export interface UseModuleCompanyPermissionsResult extends CompanyPermissionSummary {
  effectiveCompanyId: string | null;
  isGlobalScope: boolean;
  summary: CompanyPermissionSummary;
  hasPermission: (action: string) => boolean;
}

export function useModuleCompanyPermissions(
  options: UseModuleCompanyPermissionsOptions
): UseModuleCompanyPermissionsResult {
  const companies = useSelector((state: RootState) => state.auth?.companies ?? []) as CompanyData[];
  const globalRoles = useSelector((state: RootState) => state.auth?.globalRoles ?? []);
  const selectedCompany = useSelector((state: RootState) => state.auth?.selectedCompany ?? null);
  const hasGlobalRoleFlag = useSelector((state: RootState) => state.auth?.hasGlobalRole ?? false);

  const loginCompanyId = useMemo(
    () =>
      String(selectedCompany?.company_id || getCurrentCompanyId() || '').trim() || null,
    [selectedCompany?.company_id]
  );

  const resolved = useMemo(
    () =>
      resolveCompanyPermissions({
        companies,
        companyId: options.companyId,
        resource: options.resource,
        globalRoles,
        selectedCompany,
        hasGlobalRole: hasGlobalRoleFlag,
        loginCompanyId,
      }),
    [
      companies,
      options.companyId,
      options.resource,
      globalRoles,
      selectedCompany,
      hasGlobalRoleFlag,
      loginCompanyId,
    ]
  );

  const hasPermission = useMemo(() => {
    return (action: string) =>
      hasPermissionInCompany({
        companies,
        companyId: options.companyId,
        resource: options.resource,
        action,
        globalRoles,
        selectedCompany,
        hasGlobalRole: hasGlobalRoleFlag,
        loginCompanyId,
      });
  }, [
    companies,
    options.companyId,
    options.resource,
    globalRoles,
    selectedCompany,
    hasGlobalRoleFlag,
    loginCompanyId,
  ]);

  return {
    canRead: resolved.canRead,
    canCreate: resolved.canCreate,
    canUpdate: resolved.canUpdate,
    canDelete: resolved.canDelete,
    effectiveCompanyId: resolved.effectiveCompanyId,
    isGlobalScope: resolved.isGlobalScope,
    summary: resolved.summary,
    hasPermission,
  };
}

export default useModuleCompanyPermissions;
